import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { merge } from 'ts-deepmerge';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import type { GigawidgetSettings } from '../../universal-widget/util';
import { useDashboardStore } from '../../useDashboardStore';
import type {
  DataSourceVariables,
  DateFilterOptions,
  WidgetDataSource,
} from '../types';

const applyFilter = <T, K>(filterFunc?: (value: T) => K, value?: T) => {
  if (Array.isArray(value) && value.length === 0) {
    return {};
  }
  if (value == null) {
    return {};
  }
  if (!filterFunc) {
    return {};
  }

  return filterFunc(value);
};

/**
 * The dashboard date range mapped through a `dateFilter` callback, WITHOUT the
 * `ignoreDashboardDateFilter` gate `useDataSourceFilter` applies.
 *
 * The two seams answer different questions. `ignoreDashboardDateFilter` means
 * "keep the dashboard's date range off my ROWS" — which all six over-time
 * charts set, since narrowing the rows would drop whole lines from the chart
 * rather than shortening them. Clipping their NESTED result series is a
 * separate concern that must apply either way: pre-COI-600 those widgets
 * expressed it as a raw `variables` block (`resultsWhere`,
 * `riskAssessmentResultsWhere`, …) that never passed through this hook, so the
 * gate never touched it.
 *
 * COI-600 routed the clip through `dateFilterOptions` and so put it behind the
 * gate, silently disabling it — a date-filtered dashboard on the coi-600
 * ephemeral env sent `getIndicators { resultsWhere: {} }`. Keep the seams
 * separate; do not fold this back into `useDataSourceFilter`.
 */
export const useSeriesDateFilter = <T extends WidgetDataSource>(
  options?: DateFilterOptions<T>
) => {
  const { filters } = useDashboardStore();

  return applyFilter(
    (dateRange) =>
      options?.dateFilter?.(dateRange, options?.precision ?? 'day') ?? {},
    filters.dateRange
  ) as Partial<DataSourceVariables<T>>;
};

export const useDataSourceFilter = <T extends WidgetDataSource>(
  dataSource: T,
  dateFilterOptions?: DateFilterOptions<T>
) => {
  const { filters, myItemsFilters } = useDashboardStore();
  const [settings] = useDashboardWidgetSettings<GigawidgetSettings>();
  const { user } = useRisksmartUser();

  const departmentFilter = applyFilter(
    dataSource.dashboardFilterConfig.departmentsFilter,
    filters.departments
  );
  const tagFilter = applyFilter(
    dataSource.dashboardFilterConfig.tagsFilter,
    filters.tags
  );
  const dateFilter = applyFilter(
    (dateRange) =>
      dateFilterOptions?.dateFilter?.(
        dateRange,
        dateFilterOptions?.precision ?? 'day'
      ) ?? {},
    filters.dateRange
  );

  const ownershipFilter = applyFilter(
    (ownershipFilters) =>
      dataSource.dashboardFilterConfig.ownershipFilter?.(
        ownershipFilters,
        user?.userId
      ) ?? {},
    myItemsFilters
  );

  if (settings?.allowOwnershipFiltering) {
    return ownershipFilter as DataSourceVariables<T>;
  }

  return merge(
    departmentFilter,
    tagFilter,
    settings?.ignoreDashboardDateFilter ? {} : dateFilter
  ) as DataSourceVariables<T>;
};
